import { createContext, useContext, useEffect, useState, useRef } from 'react'

import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import LinearProgress from '@mui/material/LinearProgress'
import Avatar from '@mui/material/Avatar'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import {
  Search as SearchIcon,
  Group as GroupIcon,
  Security as SecurityIcon,
  Circle as CircleIcon,
  Info as InfoIcon,
  Person as PersonIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material'

import { AddButton } from '../../../../components/global/Button'
import ActionPaper from '../../../../components/global/Widget/ActionPaper'
import PaperTitle from '../../../../components/global/Widget/ActionPaper/PaperTitle'
import { GroupPanel, GroupPanelItem, TabsPanel } from '../../../../components/global/Widget';
import UserGroupManage, { IUserGroupMemberFormValues } from '../../../../components/pages/Setting/Access/UserGroup/UserGroupManage'
import UserGroupAccessManage from '../../../../components/pages/Setting/Access/UserGroup/UserGroupAccessManage'
import UserGroupInformation, { IGroupFormValues } from '../../../../components/pages/Setting/Access/UserGroup/UserGroupInformation'
import { useGetAllGroups, useGetGroupMembersByUUID, useGetGroupPermissionByUUID } from '../../../../hook/query/groupCRUD'
import { DUserFormValues, IUserFormValues } from '../User'
import { useForm } from 'react-hook-form'
import { IPermissionData } from '../../../../components/pages/Setting/Access/User/UserMemberManage'
import { IPermission } from '../../../../compiler/global.interface'
import { yupResolver } from '@hookform/resolvers/yup'
import { object, string } from 'yup'
import { EHelperText } from '../../../../utilitie/validation'
import { reverse } from 'lodash'
export interface ISettingAccessUserGroupPageProps {
}

type TDefaultValues = {
  useInfoForm: { name: string, description: string, is_master: number },
  useMemberForm: { members: any[] }
  usePermissionForm: { permissionData: any[] }
}

export interface ISettingAccessUserGroupPageContext {
  uuid: string | null,
  setActionLog: React.Dispatch<React.SetStateAction<string>>,
  defaultValues: TDefaultValues
}

export const SettingAccessUserGroupPageContext = createContext<Partial<ISettingAccessUserGroupPageContext>>({})

export const useSettingAccessUserGroupPageContext = () => {
  let context = useContext(SettingAccessUserGroupPageContext) as ISettingAccessUserGroupPageContext
  if (!context) {
    throw new Error('can nott useSettingAccessUserGroupPageContext outside SettingAccessUserGroupPageContext')
  }
  return context
}

export interface IGroup {
  id: number | null,
  uuid: string | null,
  name: string,
  demo_name?: string,
  is_active: 1 | 0,
  description: string,
  is_master: number,
  permissionData: IPermissionData[] | []
  users: Partial<IUserFormValues>[] | []
}

export const DGroup = {
  id: null,
  uuid: '',
  name: '',
  demo_name: 'สร้างกลุ่มผู้ใช้งานใหม่',
  is_master: 0,
  is_active: 1 as 1,
  description: '',
  users: [],
  permissionData: []
}

export default function SettingAccessUserGroupPage(props: ISettingAccessUserGroupPageProps) {

  const timeoutRef: { current: NodeJS.Timeout | null } = useRef(null)

  const [currentTab, setCurretTab] = useState(0)
  const [actionLog, setActionLog] = useState('none')

  const [selectedGroup, setSelectedGroup] = useState<IGroup | null>(null)

  const [groupList, setGroupList] = useState<IGroup[] | []>([])

  const { data: groupData } = useGetAllGroups()
  const { data: groupMemberData, isLoading: groupMemberLoading, isFetching: groupMemberFetching } = useGetGroupMembersByUUID(selectedGroup?.uuid)
  const { data: groupPermissionData, isLoading:groupPermissionLoading,isFetching:groupPermissionFetching } = useGetGroupPermissionByUUID(selectedGroup?.uuid)

  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState<string>("all")

  const [defaultValues, setDefaultValues] = useState<TDefaultValues>({
    useInfoForm: { name: '', description: '', is_master: 0 },
    useMemberForm: { members: [] },
    usePermissionForm: { permissionData: [] }
  })

  const useInfoForm = useForm<IGroupFormValues>({
    mode: 'all',
    defaultValues: {
      id: '',
      name: '',
      description: ''
    },
    resolver: yupResolver(object().shape({
      name: string().required(EHelperText.REQUIRED).min(3, `${EHelperText.MIN} 3 ${EHelperText.LETTER}`).notEmpty(),
    }))
  })

  const useMemberForm = useForm<{ members: IUserGroupMemberFormValues[] }>({
    mode: 'all',
    defaultValues: {
      members: []
    }
  })

  const usePermissionForm = useForm<{ permissionData: IPermission[] }>({
    mode: 'onSubmit',
    defaultValues: {
      permissionData: []
    }
  })

  const handleSelectedGroup = (group: IGroup) => {
    setSelectedGroup(group)
  }

  // Filter groups based on search and status
  const filteredGroups = groupList.filter(group => {
    const matchesSearch = !searchTerm || 
      group.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      group.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      group.demo_name?.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesStatus = statusFilter === "all" || 
      (statusFilter === "active" && group.is_active === 1) ||
      (statusFilter === "inactive" && group.is_active === 0)

    return matchesSearch && matchesStatus
  })

  const handleAddGroup = () => {
    setActionLog('create')
    if (groupList?.every(item => item.uuid)) {
      setGroupList(prev => ([...prev, { ...DGroup }]))
    } else {
      setSelectedGroup(prev => groupList[groupList.length - 1])
    }
    let div = document.getElementById("GroupPanelScroll");
    timeoutRef.current && clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => {
      if (div) {
        div.scrollTop = div.scrollHeight - div.clientHeight + 56;
      }
    }, 100)
    setCurretTab(0)
  }

  useEffect(() => {
    if (groupData) {
      setGroupList(prev => {
        if (prev?.find(group => group.demo_name) && ['delete', 'edit'].includes(actionLog)) {
          return [...groupData, ...prev.filter(group => group.demo_name)]
        }
        return [...groupData]
      })
    }
  }, [groupData])

  useEffect(() => {
    if (groupList) {
      switch (actionLog) {
        case 'create':
          return setSelectedGroup(prev => groupList[groupList.length - 1])
        case 'edit':
          return () => { }
        case 'delete':
          return setSelectedGroup(groupList[0])
        case 'cancel-create':
          setGroupList(prev => groupList?.filter(item => !item.demo_name))
          return setActionLog('create')
        default:
          return setSelectedGroup(groupList[0])
      }
    }
  }, [groupList, actionLog])

  useEffect(() => {
    if (selectedGroup) {
      //Info Form
      let resetUseInfoForm = {
        id: selectedGroup.id,
        name: selectedGroup.name,
        description: selectedGroup.description,
        is_master: selectedGroup?.is_master,
        uuid: selectedGroup?.uuid || '',
      }

      defaultValues.useInfoForm = resetUseInfoForm
      useInfoForm.reset(resetUseInfoForm)
    }

  }, [selectedGroup])

  useEffect(() => {
    if(groupMemberData){
      let resetUseMemberForm = {
        members: groupMemberData.map(user => ({ id: user.id, is_remove: 0, avatar: user.user.avatar, firstname: user.user.firstname, lastname: user.user.lastname, user_uuid: user.user.uuid, uuid: user.user.uuid, value: user.user.uuid, option: user?.user?.firstname + ' ' + user?.user?.lastname }))
      }
      
      defaultValues.useMemberForm = resetUseMemberForm
      useMemberForm.reset(resetUseMemberForm)
    }
  }, [groupMemberData])

  useEffect(() => {
    if (groupPermissionData) {
      let resetUsePermissionForm = {
        permissionData: groupPermissionData?.permissionData as IPermission[]
      }

      defaultValues.usePermissionForm = resetUsePermissionForm
      usePermissionForm.reset(resetUsePermissionForm)
    }
  }, [groupPermissionData])

  const isLoadingContent = !!(groupMemberLoading || groupMemberFetching || groupPermissionLoading || groupPermissionFetching)

  // Calculate summary stats
  const summaryStats = {
    total: groupList?.length || 0,
    active: groupList?.filter(g => g.is_active === 1)?.length || 0,
    inactive: groupList?.filter(g => g.is_active === 0)?.length || 0,
  }

  // Count members for badge
  const membersCount = useMemberForm.watch('members')?.length || 0

  // Count active permissions for badge
  const activePermissionsCount = groupPermissionData?.permissionData?.filter((p: IPermission) => 
    p.read === 1 || p.create === 1 || p.update === 1 || p.delete === 1 || p.approve === 1
  )?.length || 0

  return (
    <SettingAccessUserGroupPageContext.Provider value={{ uuid: selectedGroup?.uuid, setActionLog, defaultValues }}>
      <ActionPaper header={{
        title: 'การจัดการกลุ่มผู้ใช้งาน',
        flexEnd: (
          <AddButton onClick={handleAddGroup}>เพิ่มกลุ่มผู้ใช้งาน</AddButton>
        )
      }}>
        <Grid container sx={{ height: '100%', overflow: 'hidden' }}>
          <Grid item xs={12} md={4} lg={3} sx={{ border: 0, height: '100%' }}>
            <GroupPanel title={'กลุ่มผู้ใช้งาน'} sx={{ border: 0 }}>
              {/* Summary Stats */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Box 
                  display="flex" 
                  gap={1} 
                  mb={2}
                >
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      flex: 1, 
                      p: 1.5, 
                      textAlign: 'center', 
                      bgcolor: 'primary.50',
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="h5" fontWeight="bold" color="primary.main">
                      {summaryStats.total}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">ทั้งหมด</Typography>
                  </Paper>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      flex: 1, 
                      p: 1.5, 
                      textAlign: 'center', 
                      bgcolor: 'success.50',
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="h5" fontWeight="bold" color="success.main">
                      {summaryStats.active}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">ใช้งาน</Typography>
                  </Paper>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      flex: 1, 
                      p: 1.5, 
                      textAlign: 'center', 
                      bgcolor: 'grey.100',
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="h5" fontWeight="bold" color="text.secondary">
                      {summaryStats.inactive}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">ไม่ใช้งาน</Typography>
                  </Paper>
                </Box>

                {/* Search */}
                <TextField 
                  size="small" 
                  placeholder="ค้นหากลุ่ม..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 1 }}
                />
                <ToggleButtonGroup 
                  size="small" 
                  value={statusFilter}
                  exclusive
                  onChange={(e, newValue) => newValue && setStatusFilter(newValue)}
                  fullWidth
                  sx={{
                    '& .MuiToggleButton-root': {
                      fontSize: '0.75rem',
                      py: 0.5
                    }
                  }}
                >
                  <ToggleButton value="all">ทั้งหมด</ToggleButton>
                  <ToggleButton value="active">ใช้งาน</ToggleButton>
                  <ToggleButton value="inactive">ไม่ใช้งาน</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Group List */}
              <Box sx={{ overflow: 'auto', flex: 1 }}>
                {filteredGroups?.map((group, index) => (
                  <GroupPanelItem 
                    onClick={() => handleSelectedGroup(group)} 
                    key={index} 
                    active={selectedGroup?.uuid === group.uuid}
                    sx={{
                      transition: 'all 0.2s ease-in-out',
                      cursor: 'pointer',
                      position: 'relative',
                      '&:hover': {
                        transform: 'translateX(4px)',
                        bgcolor: 'action.hover',
                        '& .group-avatar': {
                          transform: 'scale(1.05)',
                        },
                        '& .quick-actions': {
                          opacity: 1,
                          visibility: 'visible',
                        }
                      },
                      '&.active': {
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        '& .MuiChip-root': {
                          bgcolor: 'rgba(255,255,255,0.2)',
                          color: 'inherit'
                        },
                        '& .quick-actions': {
                          '& .MuiIconButton-root': {
                            color: 'primary.contrastText',
                            '&:hover': {
                              bgcolor: 'rgba(255,255,255,0.2)',
                            }
                          }
                        }
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2} width="100%">
                      {/* Group Avatar with indicator */}
                      <Box position="relative">
                        <Box 
                          className="group-avatar"
                          sx={{
                            width: 44,
                            height: 44,
                            border:'2px solid',
                            borderColor: group.is_active ? 'success.main' : 'grey.400',
                            transition: 'transform 0.2s ease-in-out'
                          }} 
                          flexShrink={0} 
                          bgcolor={group?.demo_name ? 'warning.main' : group.is_active ? 'primary.main' : 'grey.600'} 
                          borderRadius={'100%'} 
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <GroupIcon sx={{ color: 'white', fontSize: 22 }} />
                        </Box>
                        {group.uuid && (
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 0,
                              right: 0,
                              width: 14,
                              height: 14,
                              borderRadius: '50%',
                              bgcolor: group.is_active ? 'success.main' : 'grey.400',
                              border: '2px solid white',
                            }}
                          />
                        )}
                      </Box>
                      <Box flex={1} overflow="hidden">
                        <Typography variant="body2" fontWeight="bold" noWrap>
                          {group?.demo_name || group.name}
                        </Typography>
                        {group.description && (
                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {group.description}
                          </Typography>
                        )}
                        <Box display="flex" gap={0.5} mt={0.5} flexWrap="wrap">
                          {group.users?.length > 0 && (
                            <Chip 
                              icon={<PersonIcon sx={{ fontSize: '0.75rem !important' }} />}
                              label={`${group.users.length} สมาชิก`} 
                              size="small" 
                              variant="outlined"
                              sx={{ fontSize: '0.65rem', height: 20 }}
                            />
                          )}
                          {group.is_master === 1 && (
                            <Chip 
                              label="Super Admin" 
                              size="small" 
                              color="warning"
                              sx={{ fontSize: '0.65rem', height: 20 }}
                            />
                          )}
                        </Box>
                      </Box>

                      {/* Quick Actions */}
                      {group.uuid && (
                        <Box 
                          className="quick-actions"
                          sx={{
                            position: 'absolute',
                            right: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            display: 'flex',
                            gap: 0.5,
                            opacity: 0,
                            visibility: 'hidden',
                            transition: 'opacity 0.2s ease-in-out',
                            bgcolor: 'background.paper',
                            borderRadius: 1,
                            boxShadow: 1,
                            p: 0.5,
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Tooltip title="Copy Group Name">
                            <IconButton 
                              size="small"
                              onClick={() => {
                                navigator.clipboard.writeText(group.name || '')
                              }}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </GroupPanelItem>
                ))}
                
                {filteredGroups.length === 0 && (
                  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={4}>
                    <GroupIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      ไม่พบกลุ่มผู้ใช้งาน
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ลองปรับเงื่อนไขการค้นหา
                    </Typography>
                  </Box>
                )}
              </Box>
            </GroupPanel>
          </Grid>
          <Grid item xs={9} sx={{ height: '100%' }}>
            <TabsPanel currentTab={currentTab} onChange={(index) => { setCurretTab(index); return true }} sx={{ borderLeft: 0 }} tabs={[
              {
                label: 'ข้อมูลกลุ่ม',
                icon: <InfoIcon />,
                component: isLoadingContent ? <LinearProgress /> : <UserGroupInformation useInfoForm={useInfoForm} />
              },
              {
                label: 'สมาชิกกลุ่ม',
                icon: <PersonIcon />,
                badge: membersCount > 0 ? membersCount : undefined,
                badgeColor: 'info',
                disabled: !selectedGroup?.uuid,
                component: isLoadingContent ? <LinearProgress /> : <UserGroupManage useMemberForm={useMemberForm} />
              },
              {
                label: 'สิทธิ์การใช้งาน',
                icon: <SecurityIcon />,
                badge: activePermissionsCount > 0 ? activePermissionsCount : undefined,
                badgeColor: 'success',
                disabled: !selectedGroup?.uuid,
                component: isLoadingContent ? <LinearProgress /> : <UserGroupAccessManage usePermissionForm={usePermissionForm} />
              },
            ]} />
          </Grid>
        </Grid>
      </ActionPaper>
    </SettingAccessUserGroupPageContext.Provider>
  );
}
